import { autoinject, transient } from "aurelia-dependency-injection";
import { DataType, EntityFilterPropertyModel, EntityMetaDataModel, QueryFilter } from "../../../../../../../models/queries/queries.models";
import { bindable, BindingEngine } from "aurelia-framework";
import { QueryBuilder } from "../../../../../../components/queries/query-builder";
import { BindingSignaler } from "aurelia-templating-resources";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { QueriesClientService } from "../../../../../../../services/queries/queries-client.services";
import { RulesClientService } from "../../../../../../../services/rules/rules-client.service";
import { Subscription } from "aurelia-event-aggregator";
import { ArrayUtils } from "../../../../../../../utilities/arrays";
import * as Enumerable from "linq";
import { AggregateOperator } from "../../../../../../../models/rules/rules.models";


@autoinject()
@transient()
export class RulesQueryBuilder {

    private _collectionSubscriptions: Subscription[] = [];
    private _propertyChangesSubscriptions: Subscription[] = [];

    @bindable
    public shouldHideRuleParameter: boolean = false;
    @bindable
    public readonly: boolean = false;
    @bindable
    public disabled: boolean = false;
    @bindable
    public buildParameters: boolean = false;
    @bindable
    public vetId: number;
    @bindable
    public customerId: number;
    @bindable
    public businessUnitId: number;
    @bindable
    public clauses: QueryFilter[] = [];
    @bindable
    public showEntitySelection: boolean = true;
    @bindable
    public entityUri: string;
    @bindable
    public scopeProperty: string;

    public aggregateOperators: { operator: number, name: string, hideProperty?: boolean }[] = [
        { operator: AggregateOperator.Count, name: "Count", hideProperty: true },
        { operator: AggregateOperator.Sum, name: "Sum" },
        { operator: AggregateOperator.Min, name: "Min" },
        { operator: AggregateOperator.Max, name: "Max" },
        { operator: AggregateOperator.Avg, name: "Avg" },
        { operator: AggregateOperator.First, name: "First" },
        { operator: AggregateOperator.Last, name: "Last" },
        { operator: AggregateOperator.PartitionedCount, name: "Partitioned Count" },
        { operator: AggregateOperator.FirstBy, name: "First By", hideProperty: true},
        { operator: AggregateOperator.LastBy, name: "Last By", hideProperty: true}
    ];

    public currentVm: RulesQueryBuilder = this;
    public context: any;
    models: EntityMetaDataModel[] = [];

    public constructor(public whereQueryBuilder: QueryBuilder, public aggregateQueryBuilder: QueryBuilder,
        private bindingSignaler: BindingSignaler, private queriesService: QueriesClientService,
        public rulesClientService: RulesClientService, private bindingEngine: BindingEngine) {
        this.aggregateQueryBuilder.isAggregate = true;
    }

    get isAggregateQuery() {
        return this.aggregateQueryBuilder.clauses.length > 0;
    }

    set isAggregateQuery(x: boolean) {

        if (x) {
            if (this.aggregateQueryBuilder.clauses.length <= 0) {
                this.aggregateQueryBuilder.addFilter();
                this.aggregateOperatorChanged(this.aggregateQueryBuilder.clauses[0], null);
            }
        } else {
            this.aggregateQueryBuilder.clauses.splice(0, this.aggregateQueryBuilder.clauses.length);
        }
    }

    public onPropertySelected($event, $index, filter: QueryFilter): void {
        filter.filterValue = null;
        filter.filterProperty = $event.detail;
        this.bindingSignaler.signal("update-filters-html");
    }

    public onOrderByPropertySelected($event, $index, filter: QueryFilter): void {
        filter.orderByProperty = $event.detail;
        this.bindingSignaler.signal("update-filters-html");
    }

    public removeFilter(item: QueryFilter, x: QueryBuilder): void {
        const index = x.clauses.indexOf(item);
        if (x.clauses[index].subscription && x.clauses[index].subscription.dispose) {
            x.clauses[index].subscription.dispose();
        }

        if (index > -1) {
            x.clauses.splice(index, 1);
            if (x.isPropertySelected[index]) {
                x.isPropertySelected[index].isSelected = true;
            }
        }
    }

    getFilterProperties(uris: string[], qb: QueryBuilder): EntityMetaDataModel[] {
        const propertiesToHide = qb.propertiesToHide ? qb.propertiesToHide : [];

        const lowerUris = uris.map(x => x.toLowerCase());
        const models = this.models.filter(x => lowerUris.indexOf(x.uri.toLowerCase()) > -1);
        return Enumerable.from(models).select(a => {
            const clone = CloneHelper.cloneObject(a);
            let props = clone.filterProperties.filter(x => propertiesToHide.every(y => y !== x.name));
            if (qb.sortAlphabetically)
                props = this.sortProperties(props);

            clone.filterProperties = props;
            return clone;
        }).toArray();
    }

    aggregateOperatorChanged(filter: QueryFilter, prevValue) {
        this.setAggregateModels();
        //clear selections when aggregate operator is changed
        if(prevValue && prevValue?.operator != filter?.aggregateOperator)
        {
            filter.filterOperator = null;
            filter.filterValue = null;
            filter.filterProperty = null;
        }
        const selectedModelIndex = this.entityUri && this.aggregateQueryBuilder.models.length > 1 ? this.aggregateQueryBuilder.models.findIndex(x => x.uri == this.entityUri) : 0;
        const model = this.aggregateQueryBuilder.models[selectedModelIndex];

        if (filter.aggregateOperator == AggregateOperator.Count) {
            filter.uri = model.uri;
            filter.joiningClause = null;
            filter.filterProperty = {
                friendlyName: "",
                type: DataType.Number,
                isDynamicProperty: false,
                isNullable: false
            };
        }
        else {

            if(!filter.uri && model.uri)
            {
                filter.uri = model.uri;
            }

            if(!filter.joiningClause)
            {
                
                const joiningClause = this.scopeProperty? 
                    model.joiningProperties.filter(x => x.joiningVetContextProperty == this.scopeProperty)
                    : (this.clauses?.filter(x => x.joiningClause)?.length > 0? this.clauses.filter(x => x.joiningClause).map(x => x.joiningClause): []);
                filter.joiningClause = joiningClause.length > 0? joiningClause[0]: null;
            }
        }
    }

    sortProperties(filterProperties: EntityFilterPropertyModel[]): EntityFilterPropertyModel[] {
        for (let i = 0; i < filterProperties.length; i++) {
            let min = i;
            for (let j = i; j < filterProperties.length; j++) {
                if (filterProperties[j].friendlyName <
                    filterProperties[min].friendlyName) {
                    min = j;
                }
            }

            if (min !== i) {
                const t = filterProperties[min];
                filterProperties[min] = filterProperties[i];
                filterProperties[i] = t;
            }
        }

        return filterProperties;
    }

    public getPropertyOperators(x: QueryBuilder, filter: QueryFilter) {
        if (filter.aggregateOperator == AggregateOperator.PartitionedCount)
        {
            return CloneHelper.cloneObject(x.operators.operatorsForSingleValues[DataType.Number].filter(x => !x.isOnlyForNullableProperties));
        }

        return CloneHelper.cloneObject(x.getPropertyOperators(filter));
    }

    defaultQueryBuilder: string = "./query-builders/default-query-builder";

    getQueryBuilder(uri: string) {
        require(`views/pages/modules/rules/dialogs/add-rule/builder/query-builders/default-query-builder.ts`);
        require(`views/pages/modules/rules/dialogs/add-rule/builder/query-builders/default-query-builder.html`);
        if (uri) {
            const uriLower = uri.toLowerCase();
            if (uriLower == "vettingruleforms" || uriLower == 'vettingrulevesselvets'
                || uriLower == 'vettingrulevesselautoscreening'
                || uriLower == "vettingrulelistitems"
                || uriLower == "vettingruleparameters") {
                    require(`views/pages/modules/rules/dialogs/add-rule/builder/query-builders/${uriLower}-query-builder.ts`);
                    require(`views/pages/modules/rules/dialogs/add-rule/builder/query-builders/${uriLower}-query-builder.html`);
                    return `./query-builders/${uriLower}-query-builder`;
            }
        }

        return this.defaultQueryBuilder;
    }

    public onEntitySelected(filter: QueryFilter, x: QueryBuilder, signal: string): void {
        if (filter && !filter.isAggregate)
            filter.filterProperty = null;
        if (x == this.whereQueryBuilder) {
            this.setAggregateModels();
        }


        this.bindingSignaler.signal(signal);
    }

    getJoiningProperties(uri: string) {
        return this.models.filter(x => x.uri == uri).map(x => x.joiningProperties)[0];
    }


    async attached() {
        const operators = await this.queriesService.getQueryOperators();
        this.models = await this.rulesClientService.getEntityModels();
        this.whereQueryBuilder.models = this.getFilterProperties(this.models.map(x => x.uri), this.whereQueryBuilder);
        this.whereQueryBuilder.operators = operators;
        this.whereQueryBuilder.metaDataLoaded = true;


        this.setAggregateModels();
        this.aggregateQueryBuilder.operators = operators;
        this.aggregateQueryBuilder.metaDataLoaded = true;

        this._propertyChangesSubscriptions.push(this.bindingEngine.propertyObserver(this.whereQueryBuilder, 'clauses')
            .subscribe(() => {
                this.unsubscribeForCollectionChanges();
                this.subscribeForCollectionChanges();
            }));

        this._propertyChangesSubscriptions.push(this.bindingEngine.propertyObserver(this.aggregateQueryBuilder, 'clauses')
            .subscribe(() => {
                this.unsubscribeForCollectionChanges();
                this.subscribeForCollectionChanges();
            }));
    }

    public bind(bindingContext: any): void {
        this.context = bindingContext;

        this.clausesChanged();
    }

    public clausesChanged = (): void => {
        this.resetQueryBuilder();
        if (this.clauses) {
            this.unsubscribeForCollectionChanges();
            this.subscribeForCollectionChanges();
        }
    }

    onClausesCollectionModified = (changedValues: any, clauses: QueryFilter[]) => {
        if (changedValues && changedValues?.length > 0) {
            for (let i = 0; i < changedValues.length; i++) {
                const removed: QueryFilter[] = changedValues[i].removed;
                const countNew: number = changedValues[i].addedCount;
                const startIndex: number = changedValues[i].index;
                const addedValues: QueryFilter[] = clauses.slice(startIndex, startIndex + countNew);

                removed.forEach(x => {
                    const indexClauses = ArrayUtils.findIndex(this.clauses, y => x == y);
                    this.clauses.splice(indexClauses, 1);
                });

                addedValues.forEach(x => {
                    this.clauses.push(x);
                });
            }

            this.setAggregateModels();
        }
    }

    private setAggregateModels() {
        let aggregateModels = this.clauses ? this.clauses.filter(x => x.uri && !x.isAggregate && this.getQueryBuilder(x.uri) == this.defaultQueryBuilder).map(x => x.uri) : [];
        if(this.entityUri)
            aggregateModels = this.models ? this.models.filter(x => x.uri == this.entityUri).map(x => x.uri) : [];
        this.aggregateQueryBuilder.models = [<EntityMetaDataModel>{
            uri: "",
            name: "",
            entityType: -1,
            functions: [],
            joiningProperties: [],
            projectionProperties: [],
            filterProperties: [
                {
                    friendlyName: "",
                    type: DataType.Number,
                    isDynamicProperty: false,
                    isNullable: false
                }
            ]
        }].concat(this.getFilterProperties(aggregateModels, this.aggregateQueryBuilder));

        const selectedModelIndex = this.entityUri && this.aggregateQueryBuilder.models.length > 1 ? this.aggregateQueryBuilder.models.findIndex(x => x.uri == this.entityUri) : 0;

        this.aggregateQueryBuilder.propValue = this.aggregateQueryBuilder.models[selectedModelIndex]?.uri;
    }

    private unsubscribeForCollectionChanges() {
        if (this._collectionSubscriptions) {

            this._collectionSubscriptions.forEach(sub => {
                sub.dispose();
            });
        }
    }

    private subscribeForCollectionChanges() {
        this._collectionSubscriptions.push(this.bindingEngine.collectionObserver(this.whereQueryBuilder.clauses)
            .subscribe(x => this.onClausesCollectionModified(x, this.whereQueryBuilder.clauses)));

        this._collectionSubscriptions.push(this.bindingEngine.collectionObserver(this.aggregateQueryBuilder.clauses)
            .subscribe(x => this.onClausesCollectionModified(x, this.aggregateQueryBuilder.clauses)));
    }

    detached() {
        this.unsubscribeForCollectionChanges();
        if (this._propertyChangesSubscriptions) {

            this._propertyChangesSubscriptions.forEach(sub => {
                sub.dispose();
            });
        }
    }

    private resetQueryBuilder() {
        if (this.clauses) {
            this.whereQueryBuilder.clauses = this.clauses.filter(x => !x.isAggregate);
            this.aggregateQueryBuilder.clauses = this.clauses.filter(x => x.isAggregate);
        }

        this.whereQueryBuilder.clausesChanged();
        this.aggregateQueryBuilder.clausesChanged();
    }

    entityUriChanged()
    {
        this.setAggregateModels();
    }
    
    scopePropertyChanged()
    {
        this.setAggregateModels();
    }
}